import { LayerVisualizationSettings } from 'components/Procedure/Infobar/slidesVisualizationAndConfiguration';
import { StudyExternalStorageSettings } from './externalStorage';
import { InferredFeatureConfig } from './inferredFeatures';
import { MultiplexPanelApproval, MultiplexPanelMembership, MultiplexPanelRevision } from './multiplexPanels';
import { QualityControlLabel } from './qualityControlLabels';

/* eslint-disable camelcase */
export const NR_VALUE = 99999;
export const NA_VALUE = -1;

export const studyValueDisplayText = (value: number): string => {
  if (value === NA_VALUE) {
    return 'NA';
  }
  if (value === NR_VALUE) {
    return 'NR';
  }
  return value.toFixed(2);
};

interface PositiveGroupSize {
  percentile: number;
  features: Feature[];
}

interface VariateData {
  endpoint: string;
  patients?: number;
  overallResponseRate: number;
  medianTimeToFailure: number;
  medianOverallSurvival: number;
  positiveGroupSizes: PositiveGroupSize[];
}

interface StudyData {
  univariate?: VariateData;
  multivariate?: VariateData;
}

interface DeconvComponentSettings {
  componentStainTypeId: string;
  displayName?: string;
}

interface LinearDeconvInputs extends DeconvComponentSettings {
  // The operation performed in the shader: `linearCoefficients * pixelOpticalDensity + intercept` in the optical density space [0-1]
  deconvolutionType: 'linear';
  /*
   * 3 "rows" of 3 coefficients for each RGB channel in the linear transformation (c_1*r + c_2*g + c_3*b)
   * organized in column-major order:
   * [[r_coeff_1, g_coeff_1, b_coeff_1], [r_coeff_2, g_coeff_2, b_coeff_2], [r_coeff_3, g_coeff_3, b_coeff_3]]
   * The coefficients are in the optical density space [0-1]
   */
  coefficients: [[number, number, number], [number, number, number], [number, number, number]];
  intercept?: number[]; // Optional, defaults to [0, 0, 0] (Optical Density)
}

interface CubicDeconvInputs extends DeconvComponentSettings {
  // Applies cubic polynomial feature transformation and 20x3 matrix multiplication to transform RGB texture colors (in optical density [0-1])
  deconvolutionType: 'cubic';
  // three "rows" of 20 coefficients for the cubic polynomial
  // (c_1      + c_2*r      + c_3*g      + c_4*b      + c_5*r^2    + c_6*r*g    + c_7*r*b  + c_8*g^2    + c_9*g*b    + c_10*b^2 +
  //  c_11*r^3 + c_12*r^2*g + c_13*r^2*b + c_14*r*g^2 + c_15*r*g*b + c_16*r*b^2 + c_17*g^3 + c_18*g^2*b + c_19*g*b^2 + c_20*b^3)
  coefficients: [
    [number, ...number[]], // tuple of 20 numbers
    [number, ...number[]], // tuple of 20 numbers
    [number, ...number[]] // tuple of 20 numbers
  ];
  // This is technically redundant with (c_20), but it may by used in the regression to find an optimal deconvolution matrix
  intercept?: number[]; // Optional, defaults to [0, 0, 0] (Optical Density).
}

export type DeconvolutionOperation = LinearDeconvInputs | CubicDeconvInputs;

export interface CalculationSettingsMultiStain {
  [stain: string]: {
    [stain: string]: number[];
  };
}
export interface CalculationSettingsSingleStain {
  [stain: string]: number[];
}

export interface StudyDeconvolutionSettings {
  stainToComponentOperation: { [stain: string]: DeconvolutionOperation[] };
  calculationSettings: CalculationSettingsMultiStain | CalculationSettingsSingleStain;
}

export enum AnalysisStudioTab {
  NucleaiResults = "Nucleai's Results",
  Overview = 'Overview',
  DataExploration = 'Data Exploration',
  QaAnalysis = 'QA Analysis',
  QcAnalysis = 'QC Analysis',
  MifMarkerUnsupervisedAnalysis = 'mIF Marker Unsupervised Analysis',
  CellNeighborhoods = 'Cell Neighborhoods',
  MySavedResults = 'My Saved Results',
  NucleaiCopilot = 'Nucleai Copilot',
  StudyReport = 'Study Report',
}

export interface OrchestrationClusterReassignmentOption {
  metadata?: {
    createdAt: string;
    createdBy: string;
    updatedAt: string;
    updatedBy: string;
    slideIds: string[];
  }; // Can be used later to support multiple cluster reassignments per orchestration, i.e. for different k values, etc.
  reassignments: {
    [fromClusterId: number]: string; // Maps from from generated cluster ID to a typed (from database) cluster ID (string)
  };
}

export interface ClusterIdReassignments {
  [orchestrationId: string]: OrchestrationClusterReassignmentOption[];
}

export type BrightfieldPostIngestStep = 'cellSegmentation';
export type MifPostIngestStep = 'normalization' | 'cellSegmentation';

export interface StudyPostIngestPipelineSettings {
  brightfield: {
    additionalSteps: BrightfieldPostIngestStep[];
  };
  mIF: {
    additionalSteps: MifPostIngestStep[];
    dnaTilePercentileCapsEnabled?: boolean;
  };
}

export interface StudyWorkflowModules {
  rnaProbes?: boolean;
  markerPositivity?: boolean;
  cellRulesBasedCellTyping?: boolean;
}

export interface StudyWorkflowSettings {
  modules?: StudyWorkflowModules;
}

export interface StudyMifSettings {
  peakDetectionUseRawDog?: boolean;
}

export type StudyWorkflowStepApprovalAction = 'approved' | 'revoked';

export interface StudyWorkflowStepApproval {
  stepKey: string;
  // Defaults to 'approved' server-side for entries persisted before this field existed.
  action: StudyWorkflowStepApprovalAction;
  approvedAt: string;
  approvedBy: string;
  // Returned by the approve request for display only; not part of the persisted entry
  approvedByName?: string | null;
  // Present only when the user accepted the step's displayed snapshot.
  stateDigest?: string | null;
  stateSummary?: string | null;
}

export interface StudyRnaProbeDisplayNameOverride {
  probeId: string;
  displayName: string;
  setAt: string;
  setBy: string;
}

export interface MultiplexPanelCellRuleSnapshot {
  targetCellTypePath: string;
  rules: Record<string, number>;
  sourceCellTypePath?: string | null;
}

export interface MultiplexPanelCellRules {
  panelId?: string | null;
  revision?: number;
  markerIds?: string[];
  cellRulePanelId?: string | null;
  rules: MultiplexPanelCellRuleSnapshot[];
  savedAt: string;
  savedBy: string;
  jobId?: string | null;
  name?: string | null;
}

export interface CellTypingRequestedOutputsSettings {
  positivity: boolean;
  cellTypes: boolean;
  hScores: boolean;
}

export interface CellTypingPanelSettings {
  panelId: string;
  savedAt: string;
  savedBy: string;
  cellRuleRevision?: number | null;
  configuration: Record<string, unknown>;
  requestedOutputs?: CellTypingRequestedOutputsSettings | null;
}

export interface CellTypingPanelSettingsSaveResponse extends CellTypingPanelSettings {
  cellRuleName?: string | null;
  reusedExistingRevision: boolean;
  revision?: MultiplexPanelCellRules | null;
}

export type PlatformVersionPolicy = 'locked' | 'latest';

export interface StudyPlatformPolicySettings {
  versionPolicy?: PlatformVersionPolicy;
}

export interface StudyPlatformVersionSyncResult {
  versionPolicy: PlatformVersionPolicy;
  previousDockerImage: string | null;
  dockerImage: string | null;
  updated: boolean;
}

export interface Study {
  id: string;
  externalStudyId: string;
  name: string;
  description: string;
  cancerTypeId: string;
  date: string;
  labId: string;
  allowExport: boolean;
  data?: StudyData;
  generalComments: string;
  analysisStartTime: string;
  serviceType: string;
  highlightedFeatures: string[];
  hiddenFeatures: string[];
  defaultOverviewPresetId?: string;
  qcLabelsConfig: QualityControlLabel[];
  inferredFeaturesConfig: InferredFeatureConfig[];
  settings?: {
    hiddenUploadKeys?: Record<string, { hiddenAt: string | null; hiddenBy: string | null }>;
    // If set, slides of this staining type will be shown by default
    defaultStainingType?: string;
    // If set, the study will be shown in this view mode by default
    defaultSlideViewMode?: 'single' | 'side-by-side';
    // If set, the slides in the study will use the DeckGL viewer
    useOSDViewer?: boolean;
    defaultMultiplexColorPreset?: string; // ID of the default multiplex color preset
    artifactDetailsEnabled?: boolean;
    deconvolutionSettings?: StudyDeconvolutionSettings;
    availableAlgorithms?: string[];
    // The bucket name is used as a part of an AWS secrets key to map to it's credentials and service region
    exportBucket?: { bucketName: string };
    externalStorage?: StudyExternalStorageSettings;
    heatmapsSettings?: {
      hiddenOptions?: {
        [stainTypeId: string]: {
          [heatmapKey: string]: string[]; // Array of the settings key for hidden options
        };
      };
      hiddenHeatmaps?: {
        [stainTypeId: string]: string[]; // Array of the settings key for hidden heatmaps
      };
      defaultVisualizationSettings?: {
        [stainTypeId: string]: {
          [heatmapKey: string]: {
            [optionKey: string]: Omit<Partial<LayerVisualizationSettings>, 'show' | 'selected'>;
          };
        };
      };
    };
    clusterIdReassignments?: ClusterIdReassignments;
    qcMarkersSettings?: {
      [markerId: string]: string;
    };
    postIngestPipeline?: StudyPostIngestPipelineSettings;
    mIF?: StudyMifSettings;
    workflow?: StudyWorkflowSettings;
    // Shared hidden slide IDs for Workflow, Uploads and case organization, scoped to the source study.
    dismissedCaseOrganizationSlideIds?: string[];
    // Append-only audit trail; the current approval for a step is its most recent entry (by approvedAt).
    workflowStepApprovals?: StudyWorkflowStepApproval[];
    multiplexPanels?: MultiplexPanelRevision[];
    multiplexPanelApprovals?: MultiplexPanelApproval[];
    multiplexPanelMemberships?: MultiplexPanelMembership[];
    // Append-only audit trail; the current display name for a probe is its most recent entry (by setAt).
    rnaProbeDisplayNameOverrides?: StudyRnaProbeDisplayNameOverride[];
    multiplexPanelCellRules?: MultiplexPanelCellRules[];
    // Current cell-typing setup per panel. Not the cell-rule revision history.
    cellTypingPanelSettings?: CellTypingPanelSettings[];
    // Unset means the lab default applies, falling back to 'locked'.
    platform?: StudyPlatformPolicySettings;
  };
  platformSettings?: {
    dockerImage?: string;
  };
  slidesCount: number;
  casesCount: number;
  hasFeatures?: boolean;
  hasResults?: boolean;
  // Set only on the single-study read with statistics, and only for users who may view unpublished results.
  hasUnpublishedFeatures?: boolean;
  hasUnpublishedCellTables?: boolean;
  // Of those, the ones that are internally approved.
  hasInternallyApprovedFeatures?: boolean;
  hasInternallyApprovedCellTables?: boolean;
  countInManifest?: number;
  countIngestedInManifest?: number;
  usersAcl: string[];
  studyAdmins: string[];
  // Server-resolved display names for settings.workflowStepApprovals approvers, keyed by user id
  workflowStepApproverNames?: Record<string, string> | null;
}

export interface StudyUpdate {
  id: string;
  name?: string;
  description?: string;
  defaultOverviewPresetId?: string;
  highlightedFeatures?: string[];
  hiddenFeatures?: string[];
  qcLabelsConfig?: QualityControlLabel[];
  settings?: Partial<Study['settings']>;
  platformSettings?: Partial<Study['platformSettings']>;
  usersAcl?: string[];
  studyAdmins?: string[];
}

export interface FeatureDistributionGraph {
  points: { x: number; y: number }[];
}

export interface KaplanMeierGraph {
  points: { month: number; survivalRate: number }[];
}

interface FeatureGraphs {
  distribution: {
    all: FeatureDistributionGraph;
    negativePrediction: FeatureDistributionGraph;
    positivePrediction: FeatureDistributionGraph;
  };
  kaplanMeier: {
    title: string;
    all: KaplanMeierGraph;
    negativePrediction: KaplanMeierGraph;
    positivePrediction: KaplanMeierGraph;
  };
}

export interface Feature {
  overallResponseRate: number;
  medianTimeToFailure: number;
  timeToFailureHazardRatio: number;
  timeToFailurePValue: number;
  medianOverallSurvival: number;
  overallSurvivalHazardRatio: number;
  overallSurvivalPValue: number;
  graphs: FeatureGraphs;
  univariateFeatureKeys: string[];
  [key: string]: number | string | FeatureGraphs | string[];
}

export interface UnivariateFeature extends Feature {
  key: string;
}

export interface MultivariateFeature extends Feature {
  name: string;
  univariateFeatureKeys: string[];
}
